// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Support Staff API for Advisor UI
 *
 * APIs for support staff to view their assigned advisors and work on their behalf.
 * Also APIs for advisors to view their support team.
 */

import api from './advisorApi';
import type {
  AssignedAdvisorInfo,
  SupportTeamMember,
  ActivityLogEntry,
  PaginatedActivityLog,
  ActivityLogFilters,
  ProfileWithSupportInfo,
  CreateProfileForAdvisorRequest,
} from '../types/support';

interface PaginatedProfiles {
  content: ProfileWithSupportInfo[];
  totalPages: number;
  totalElements: number;
  number: number;
  size: number;
}

export const supportApi = {
  // ============================================
  // Support Staff APIs (for users with SUPPORT role)
  // ============================================

  /**
   * Get all advisors assigned to the current support staff
   */
  getMyAssignedAdvisors: async (): Promise<AssignedAdvisorInfo[]> => {
    const response = await api.get<{ advisors: AssignedAdvisorInfo[]; totalAdvisors: number; totalProfiles: number; totalPendingLOAs: number }>('/support/my-advisors');
    return response.data.advisors || [];
  },

  /**
   * Get profiles for a specific assigned advisor
   */
  getAdvisorProfiles: async (
    advisorId: string,
    page: number = 0,
    size: number = 20,
    sortBy: string = 'createdAt',
    sortOrder: 'ASC' | 'DESC' = 'DESC',
    search?: string,
    status?: string
  ): Promise<PaginatedProfiles> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sortBy,
      sortOrder,
    });
    if (search) params.append('search', search);
    if (status) params.append('status', status);

    const response = await api.get<PaginatedProfiles>(
      `/support/advisors/${advisorId}/profiles?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get directly assigned profiles (profile-level assignments)
   */
  getMyDirectProfiles: async (): Promise<ProfileWithSupportInfo[]> => {
    interface MyProfilesResponse {
      profiles: ProfileWithSupportInfo[];
      totalProfiles: number;
    }
    const response = await api.get<MyProfilesResponse>('/support/my-profiles');
    return response.data.profiles || [];
  },

  /**
   * Get all profiles across all assigned advisors and direct profile assignments.
   * Aggregates data from multiple endpoints per the API spec.
   */
  getAllAssignedProfiles: async (
    page: number = 0,
    size: number = 20,
    sortBy: string = 'createdAt',
    sortOrder: 'ASC' | 'DESC' = 'DESC',
    search?: string,
    status?: string
  ): Promise<PaginatedProfiles> => {
    // Get assigned advisors first
    const advisorsResponse = await api.get<{ advisors: AssignedAdvisorInfo[]; totalAdvisors: number; totalProfiles: number; totalPendingLOAs: number }>('/support/my-advisors');
    const advisors = advisorsResponse.data.advisors || [];

    // Get directly assigned profiles
    interface MyProfilesResponse {
      profiles: ProfileWithSupportInfo[];
      totalProfiles: number;
    }
    const directProfilesResponse = await api.get<MyProfilesResponse>('/support/my-profiles');
    const directProfiles = directProfilesResponse.data.profiles || [];

    // Get profiles for each assigned advisor in parallel
    const advisorProfilesPromises = advisors.map(advisor =>
      api.get<PaginatedProfiles>(`/support/advisors/${advisor.id}/profiles?page=0&size=1000`)
        .then(res => res.data.content || [])
        .catch(() => [] as ProfileWithSupportInfo[])
    );
    const advisorProfilesArrays = await Promise.all(advisorProfilesPromises);

    // Merge all profiles and deduplicate by ID
    const allProfiles = new Map<string, ProfileWithSupportInfo>();

    // Add direct profiles
    for (const profile of directProfiles) {
      allProfiles.set(profile.id, profile);
    }

    // Add profiles from advisors
    for (const profiles of advisorProfilesArrays) {
      for (const profile of profiles) {
        if (!allProfiles.has(profile.id)) {
          allProfiles.set(profile.id, profile);
        }
      }
    }

    // Convert to array and apply client-side filtering/sorting
    let profilesArray = Array.from(allProfiles.values());

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      profilesArray = profilesArray.filter(p =>
        p.profileName.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (status) {
      profilesArray = profilesArray.filter(p => p.status === status);
    }

    // Sort
    profilesArray.sort((a, b) => {
      const aVal = String((a as unknown as Record<string, unknown>)[sortBy] || '');
      const bVal = String((b as unknown as Record<string, unknown>)[sortBy] || '');
      const cmp = aVal.localeCompare(bVal);
      return sortOrder === 'DESC' ? -cmp : cmp;
    });

    // Apply pagination
    const totalElements = profilesArray.length;
    const totalPages = Math.ceil(totalElements / size);
    const startIndex = page * size;
    const paginatedContent = profilesArray.slice(startIndex, startIndex + size);

    return {
      content: paginatedContent,
      totalPages,
      totalElements,
      number: page,
      size,
    };
  },

  /**
   * Create a new profile for an assigned advisor
   */
  createProfileForAdvisor: async (
    request: CreateProfileForAdvisorRequest
  ): Promise<ProfileWithSupportInfo> => {
    const response = await api.post<ProfileWithSupportInfo>(
      `/support/advisors/${request.advisorId}/profiles`,
      request
    );
    return response.data;
  },

  /**
   * Send LOA reminder (support staff can only send reminders, not create LOAs)
   */
  sendLOAReminder: async (
    loaId: string,
    signatoryId: string
  ): Promise<void> => {
    await api.post(`/support/loa/${loaId}/remind/${signatoryId}`);
  },

  /**
   * Send invitation email to client
   */
  sendClientInvitation: async (
    profileId: string,
    clientId: string
  ): Promise<void> => {
    await api.post(`/support/profiles/${profileId}/clients/${clientId}/invite`);
  },

  // ============================================
  // Profile Detail APIs (for support accessing profiles)
  // ============================================

  /**
   * Get profile details for an accessible profile
   */
  getProfile: async (profileId: string): Promise<ProfileWithSupportInfo> => {
    const response = await api.get<ProfileWithSupportInfo>(`/support/profiles/${profileId}`);
    return response.data;
  },

  /**
   * Get clients for an accessible profile
   * Note: Backend returns a List, we wrap it in pagination format for consistency
   */
  getProfileClients: async (profileId: string, page: number = 0, size: number = 10): Promise<{
    content: Array<{
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber?: string;
      dateOfBirth?: string;
      isPrimary: boolean;
      status: string;
    }>;
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  }> => {
    const response = await api.get(`/support/profiles/${profileId}/clients`);
    // Backend returns a List, wrap it in pagination format
    const allClients = response.data || [];
    const startIndex = page * size;
    const paginatedClients = allClients.slice(startIndex, startIndex + size);

    return {
      content: paginatedClients,
      totalElements: allClients.length,
      totalPages: Math.ceil(allClients.length / size),
      number: page,
      size: size,
    };
  },

  /**
   * Get policies for an accessible profile
   */
  getProfilePolicies: async (profileId: string, page: number = 0, size: number = 10): Promise<{
    content: Array<{
      id: string;
      policyNumber: string;
      policyType: string;
      providerId: string;
      providerName?: string;
      providerLogoUrl?: string;
      category?: string;
      policyTypeId: string;
      policyTypeName?: string;
      status: string;
      premiumAmount?: number;
      effectiveDate?: string;
      expiryDate?: string;
      ownershipType: string;
      signatories?: Array<{
        id: string;
        clientId: string;
        clientName: string;
        relationshipType: string;
        signingOrder: number;
      }>;
    }>;
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  }> => {
    const response = await api.get(`/support/profiles/${profileId}/policies?page=${page}&size=${size}`);
    return response.data;
  },

  /**
   * Update profile (support can update but not archive or reassign)
   */
  updateProfile: async (profileId: string, data: {
    profileName?: string;
    notes?: string;
    crmRelationshipNumber?: string;
  }): Promise<ProfileWithSupportInfo> => {
    const response = await api.put<ProfileWithSupportInfo>(`/support/profiles/${profileId}`, data);
    return response.data;
  },

  /**
   * Add client to profile
   */
  addClientToProfile: async (profileId: string, clientData: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber?: string;
    dateOfBirth?: string;
    isPrimary?: boolean;
  }): Promise<unknown> => {
    const response = await api.post(`/support/profiles/${profileId}/clients`, clientData);
    return response.data;
  },

  /**
   * Get LOAs for an accessible profile
   */
  getProfileLOAs: async (profileId: string, page: number = 0, size: number = 10): Promise<{
    content: Array<{
      id: string;
      profileId: string;
      brokerageId: string;
      policyIds: string[];
      docusignEnvelopeId?: string;
      signingMethod: string;
      status: string;
      documentStorageUrl?: string;
      signedDocumentUrl?: string;
      sentForSigningAt?: string;
      completedAt?: string;
      signatories: Array<{
        id: string;
        clientId: string;
        signatoryEmail: string;
        signatoryName: string;
        signingOrder: number;
        signingUrl?: string;
        status: string;
        viewedAt?: string;
        signedAt?: string;
        declinedAt?: string;
        declineReason?: string;
      }>;
      createdBy: string;
      createdAt: string;
      updatedBy?: string;
      updatedAt?: string;
    }>;
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  }> => {
    const response = await api.get(`/support/profiles/${profileId}/loas?page=${page}&size=${size}`);
    return response.data;
  },

  /**
   * Update client in profile
   */
  updateClient: async (profileId: string, clientId: string, clientData: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    dateOfBirth?: string;
  }): Promise<unknown> => {
    const response = await api.put(`/support/profiles/${profileId}/clients/${clientId}`, clientData);
    return response.data;
  },

  // ============================================
  // Advisor APIs (for viewing support team)
  // ============================================

  /**
   * Get support team members assigned to the current advisor (USER role)
   */
  getMySupportTeam: async (): Promise<SupportTeamMember[]> => {
    const response = await api.get<{ supportStaff: SupportTeamMember[] }>('/advisor/my-support-team');
    return response.data.supportStaff || [];
  },

  /**
   * Get all support staff in the brokerage (ADMIN role)
   */
  getAdminSupportStaffList: async (): Promise<SupportTeamMember[]> => {
    interface AdminSupportStaffItem {
      id: string;
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber?: string;
      photoUrl?: string;
      status: string;
      assignedAdvisorCount: number;
      assignedProfileCount: number;
      createdAt: string;
      brokerageId?: string;
      brokerageName?: string;
    }
    const response = await api.get<{ content: AdminSupportStaffItem[] }>('/admin/support-staff');
    // Transform admin response to SupportTeamMember format
    return (response.data.content || []).map(item => ({
      id: item.id,
      firstName: item.firstName,
      lastName: item.lastName,
      email: item.email,
      phoneNumber: item.phoneNumber,
      photoUrl: item.photoUrl,
      assignedAt: item.createdAt,
      assignedByName: 'Admin',
      lastActiveAt: undefined,
      actionsThisWeek: 0, // Admin view doesn't track this per staff member
    }));
  },

  // ============================================
  // Activity Log APIs (shared)
  // ============================================

  /**
   * Get activity log with filters
   * For advisors: shows their own activity and support staff activity
   * For support: shows only their own activity
   */
  getActivityLog: async (
    filters: ActivityLogFilters,
    page: number = 0,
    size: number = 20
  ): Promise<PaginatedActivityLog> => {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      actorFilter: filters.actorFilter,
    });

    if (filters.supportPersonId) {
      params.append('supportPersonId', filters.supportPersonId);
    }
    if (filters.actionType && filters.actionType !== 'all') {
      params.append('actionType', filters.actionType);
    }
    if (filters.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters.endDate) {
      params.append('endDate', filters.endDate);
    }

    const response = await api.get<PaginatedActivityLog>(
      `/advisor/activity-log?${params.toString()}`
    );
    return response.data;
  },

  /**
   * Get recent activity for a specific support staff member
   * (For advisors to see what their support team has been doing)
   */
  getSupportMemberActivity: async (
    supportMemberId: string,
    limit: number = 10
  ): Promise<ActivityLogEntry[]> => {
    // Use the activity-log endpoint with supportId filter
    const response = await api.get<{ content: ActivityLogEntry[] }>(
      `/advisor/activity-log?supportId=${supportMemberId}&size=${limit}`
    );
    return response.data.content || [];
  },

  /**
   * Get dashboard stats for support staff
   */
  getSupportDashboardStats: async (): Promise<{
    totalAdvisors: number;
    totalProfiles: number;
    pendingLOAs: number;
    actionsThisWeek: number;
  }> => {
    interface DashboardResponse {
      advisorAssignments?: { total?: number; totalProfiles?: number; totalPendingLOAs?: number };
      weeklyActions?: number;
    }
    const response = await api.get<DashboardResponse>('/support/dashboard/stats');
    const data = response.data;
    return {
      totalAdvisors: data.advisorAssignments?.total || 0,
      totalProfiles: data.advisorAssignments?.totalProfiles || 0,
      pendingLOAs: data.advisorAssignments?.totalPendingLOAs || 0,
      actionsThisWeek: data.weeklyActions || 0,
    };
  },

  /**
   * Get recent activity for support dashboard
   */
  getSupportRecentActivity: async (
    limit: number = 10
  ): Promise<ActivityLogEntry[]> => {
    interface RecentActivityItem {
      action: string;
      description: string;
      timestamp: string;
    }
    const response = await api.get<RecentActivityItem[]>(
      `/support/dashboard/recent-activity?limit=${limit}`
    );
    // Transform backend response to ActivityLogEntry format
    // Note: Backend currently returns simplified data, some fields are stubbed
    return (response.data || []).map((item, index) => ({
      id: `activity-${index}`,
      type: item.action as ActivityLogEntry['type'],
      title: item.description,
      subtitle: '',
      actorId: '',
      actorName: '',
      actorRole: 'SUPPORT' as const,
      resourceType: 'PROFILE' as const,
      resourceId: '',
      timestamp: item.timestamp,
    }));
  },

  /**
   * Get profiles requiring attention for support
   */
  getProfilesRequiringAttention: async (): Promise<ProfileWithSupportInfo[]> => {
    interface PagedResponse {
      content: ProfileWithSupportInfo[];
      totalPages: number;
      totalElements: number;
    }
    const response = await api.get<PagedResponse>(
      '/support/profiles/requiring-attention'
    );
    return response.data.content || [];
  },
};

export default supportApi;
